import React, { useEffect, useState } from "react";
import * as Icons from "@wix/wix-ui-icons-common";
import {
  Box,
  Card,
  Table,
  TableToolbar,
  Button,
  Loader,
  Text,
  Page,
  EmptyState,
  Image,
  TextButton,
  TableActionCell,
  Search,
  Dropdown,
  Layout,
  Cell,
  Badge,
  FeatureList,
  Accordion,
  accordionSectionItemBuilder,
  accordionItemBuilder,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { faqSections } from "./faqData";

export interface TrademarkRequest {
  _id: string;
  trademarkName: string;
  description: string;
  goodsAndServices: string;
  applicantName: string;
  applicantEmail: string;
  applicantAddress: string;
  applicantCity: string;
  applicantState: string;
  applicantZipCode: string;
  applicantCountry: string;
  status: "draft" | "submitted" | "in-review" | "approved" | "rejected";
  submittedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

interface TrademarkRequestsListProps {
  onCreateNew: () => void;
  onEdit: (request: TrademarkRequest) => void;
  onView: (request: TrademarkRequest) => void;
  instanceData?: any;
  onDeleteClick?: (requestId: string) => void;
  onPricingDisclosureClick?: () => void;
  onUpgradeClick?: () => void;
}

const StatusBadge = ({ status }: { status: TrademarkRequest["status"] }) => {
  const statusConfig: Record<
    string,
    {
      skin: "neutral" | "standard" | "warning" | "success" | "danger";
      label: string;
    }
  > = {
    draft: { skin: "neutral", label: "Draft" },
    submitted: { skin: "standard", label: "Submitted" },
    "in-review": { skin: "warning", label: "In Review" },
    approved: { skin: "success", label: "Approved" },
    rejected: { skin: "danger", label: "Rejected" },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <Badge skin={config.skin} size="small">
      {config.label}
    </Badge>
  );
};

const TrademarkRequestsList: React.FC<TrademarkRequestsListProps> = ({
  onCreateNew,
  onEdit,
  onView,
  instanceData,
  onDeleteClick,
  onPricingDisclosureClick,
  onUpgradeClick,
}) => {
  const [requests, setRequests] = useState<TrademarkRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<TrademarkRequest[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState({
    status: { id: "all", value: "All Statuses" },
    search: "",
  });

  const API_BASE_URL = "/api";

  // Get plan allowance based on plan name
  // starter: 1, portfolio: 2, growth: 3
  const getPlanAllowance = (planName?: string): number => {
    if (!planName) return 0; // No plan = no allowance
    const planLower = planName.toLowerCase();
    if (planLower.includes("starter")) return 1;
    if (planLower.includes("portfolio")) return 2;
    if (planLower.includes("growth")) return 3;
    return 0; // Unknown plan = no allowance
  };

  // Get current plan name from instance data
  const getCurrentPlan = (): string | undefined => {
    if (!instanceData?.instance) return undefined;
    // Get plan from billing.packageName
    const plan = instanceData.instance.billing?.packageName;
    return plan;
  };

  // Count non-draft requests
  const countNonDraftRequests = (): number => {
    return requests.filter((req) => req.status !== "draft").length;
  };

  // Check if user can create new request
  const canCreateNewRequest = (): boolean => {
    const planName = getCurrentPlan();
    const allowance = getPlanAllowance(planName);
    const nonDraftCount = countNonDraftRequests();
    return nonDraftCount < allowance;
  };

  // Handle create new with upgrade check
  const handleCreateNew = () => {
    if (!canCreateNewRequest()) {
      onUpgradeClick?.();
      return;
    }
    onCreateNew();
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    let filtered = requests;

    // Filter by status
    if (filter.status.value !== "All Statuses") {
      filtered = filtered.filter(
        (req) =>
          req.status === filter.status.value.toLowerCase().replace(" ", "-")
      );
    }

    // Filter by search
    if (filter.search) {
      const searchTerm = filter.search.toLowerCase();
      filtered = filtered.filter(
        (req) =>
          (req.trademarkName || "").toLowerCase().includes(searchTerm) ||
          (req.applicantName || "").toLowerCase().includes(searchTerm) ||
          (req.applicantEmail || "").toLowerCase().includes(searchTerm) ||
          (req.description || "").toLowerCase().includes(searchTerm)
      );
    }

    console.log("Filtering requests:", {
      total: requests.length,
      filteredCount: filtered.length,
      filterStatus: filter.status.value,
      filterSearch: filter.search,
      allRequests: requests.map((r) => ({
        _id: r._id,
        name: r.trademarkName,
        status: r.status,
      })),
      filteredRequests: filtered.map((r) => ({
        _id: r._id,
        name: r.trademarkName,
        status: r.status,
      })),
    });
    setFilteredRequests(filtered);
  }, [requests, filter]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const instance = new URLSearchParams(window.location.search).get(
        "instance"
      );
      const url = instance
        ? `${API_BASE_URL}/trademark-requests?instance=${encodeURIComponent(
            instance
          )}`
        : `${API_BASE_URL}/trademark-requests`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const requestsData = data.requests || [];
      console.log("Fetched requests:", requestsData.length, requestsData);
      setRequests(requestsData);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching trademark requests:", err);
      setError(err.message || "Failed to fetch trademark requests");
      setIsLoading(false);
    }
  };

  const handleDeleteClickInternal = (request_id: string) => {
    onDeleteClick?.(request_id);
  };

  const secondaryActions = (row: TrademarkRequest) => {
    const actions = [];

    // Only allow editing if status is draft
    if (row.status === "draft") {
      actions.push({
        icon: <Icons.Edit />,
        text: "Edit",
        onClick: () => onEdit(row),
      });
    }

    // Only allow deleting if status is draft
    if (row.status === "draft") {
      actions.push({
        icon: <Icons.Delete />,
        text: "Delete",
        onClick: () => handleDeleteClickInternal(row._id),
      });
    }

    return actions;
  };

  const columns = [
    {
      title: "Trademark Name",
      render: (row: TrademarkRequest) => (
        <TextButton onClick={() => onView(row)}>
          {row.trademarkName || "N/A"}
        </TextButton>
      ),
      width: "25%",
    },
    {
      title: "Applicant",
      render: (row: TrademarkRequest) => row.applicantName || "N/A",
      width: "20%",
    },
    {
      title: "Email",
      render: (row: TrademarkRequest) => row.applicantEmail || "N/A",
      width: "20%",
    },
    {
      title: "Status",
      render: (row: TrademarkRequest) => {
        try {
          return <StatusBadge status={row.status || "draft"} />;
        } catch (error) {
          console.error("Error rendering status for request:", row._id, error);
          return <StatusBadge status="draft" />;
        }
      },
      width: "15%",
    },
    {
      title: "Updated",
      render: (row: TrademarkRequest) => {
        try {
          if (!row.updatedAt)
            return (
              <Text size="small" secondary>
                N/A
              </Text>
            );
          const date = new Date(row.updatedAt);
          if (isNaN(date.getTime())) {
            console.warn("Invalid date for request:", row._id, row.updatedAt);
            return (
              <Text size="small" secondary>
                Invalid date
              </Text>
            );
          }
          return (
            <Text size="small" secondary>
              {date.toLocaleDateString()}
            </Text>
          );
        } catch (error) {
          console.error("Error rendering date for request:", row._id, error);
          return (
            <Text size="small" secondary>
              Error
            </Text>
          );
        }
      },
      width: "15%",
    },
    {
      title: "",
      render: (row: TrademarkRequest) => (
        <TableActionCell secondaryActions={secondaryActions(row)} />
      ),
      width: "5%",
      important: true,
      stickyActionCell: true,
    },
  ];

  if (isLoading) {
    return (
      <Page height="100vh">
        <Page.Content>
          <Box
            padding="SP6"
            direction="vertical"
            verticalAlign="middle"
            gap="SP2"
          >
            <Loader text="Loading trademark requests..." />
          </Box>
        </Page.Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page height="100vh">
        <Page.Content>
          <Box padding="SP6" direction="vertical" gap="SP2">
            <Text>{error}</Text>
            <Button
              onClick={() => {
                setError(null);
                fetchRequests();
              }}
            >
              Retry
            </Button>
          </Box>
        </Page.Content>
      </Page>
    );
  }

  if (requests.length === 0 && !isLoading) {
    return (
      <Page height="100vh">
        <Page.Header
          title="Trademark Requests"
          subtitle="Create and manage your US trademark requests."
          actionsBar={
            <Box gap="SP2">
              <Button prefixIcon={<Icons.Add />} onClick={handleCreateNew}>
                Create Request
              </Button>
              <Button
                skin="premium"
                prefixIcon={<Icons.PremiumFilled />}
                onClick={() => onUpgradeClick?.()}
              >
                Upgrade
              </Button>
            </Box>
          }
        />
        <Page.Content>
          <Layout>
            <Cell>
              <EmptyState
                image={
                  <Image width="120px" src="no_requests.png" transparent />
                }
                title="Create your first trademark request"
                subtitle="Start by creating a new trademark request to protect your brand in the United States."
                theme="page"
              >
                <TextButton
                  prefixIcon={<Icons.Add />}
                  onClick={handleCreateNew}
                >
                  Create Request
                </TextButton>
              </EmptyState>
            </Cell>
          </Layout>
        </Page.Content>
      </Page>
    );
  }

  return (
    <>
      <Page height="100vh">
        <Page.Header
          title="Trademark Requests"
          subtitle="Create and manage your US trademark requests."
          actionsBar={
            <Box gap="SP2">
              <Button prefixIcon={<Icons.Add />} onClick={handleCreateNew}>
                Create Request
              </Button>
              <Button
                skin="premium"
                prefixIcon={<Icons.PremiumFilled />}
                onClick={() => onUpgradeClick?.()}
              >
                Upgrade
              </Button>
            </Box>
          }
        />
        <Page.Content>
          <Layout>
            <Cell>
              <Card>
                <Table data={filteredRequests} columns={columns}>
                  <TableToolbar>
                    <TableToolbar.ItemGroup position="start">
                      <TableToolbar.Item>
                        <TableToolbar.Title>
                          {filteredRequests.length}{" "}
                          {filteredRequests.length === 1
                            ? "request"
                            : "requests"}
                          {filteredRequests.length !== requests.length &&
                            ` of ${requests.length}`}
                        </TableToolbar.Title>
                      </TableToolbar.Item>
                      <TableToolbar.Item>
                        <Dropdown
                          options={[
                            { id: "all", value: "All Statuses" },
                            { id: "draft", value: "Draft" },
                            { id: "submitted", value: "Submitted" },
                            { id: "in-review", value: "In Review" },
                            { id: "approved", value: "Approved" },
                            { id: "rejected", value: "Rejected" },
                          ]}
                          selectedId={filter.status.id}
                          border="round"
                          size="small"
                          popoverProps={{ appendTo: "window" }}
                          onSelect={(status: any) =>
                            setFilter({ ...filter, status })
                          }
                          valueParser={({ value }) => value}
                        />
                      </TableToolbar.Item>
                    </TableToolbar.ItemGroup>
                    <TableToolbar.ItemGroup position="end">
                      <TableToolbar.Item>
                        <Search
                          size="small"
                          onChange={(event) =>
                            setFilter({ ...filter, search: event.target.value })
                          }
                          onClear={() => setFilter({ ...filter, search: "" })}
                        />
                      </TableToolbar.Item>
                    </TableToolbar.ItemGroup>
                  </TableToolbar>
                  <Table.Titlebar />
                  <Table.Content titleBarVisible={false} />
                </Table>
              </Card>
            </Cell>
            <Cell>
              <Card>
                <Card.Content>
                  <FeatureList
                    cols={4}
                    features={[
                      {
                        image: <Icons.Document size="60px" />,
                        title: "1. Create Your Application",
                        text: "Start by creating a draft trademark request with your brand name, description, and goods/services. You can save and edit your application until you're ready to submit.",
                      },
                      {
                        image: <Icons.DocumentSend size="60px" />,
                        title: "2. Submit for Review",
                        text: "Once your application is complete, submit it for review. Our team will examine your trademark request to ensure all information is accurate and complete.",
                      },
                      {
                        image: <Icons.Email size="60px" />,
                        title: "3. Confirm & Sign Authorization",
                        text: "You'll receive an email with authorization documents to review and sign. Confirm your details and provide your electronic signature to proceed with the registration.",
                      },
                      {
                        image: <Icons.Check size="60px" />,
                        title: "4. Get Approved",
                        text: "After review and authorization, your trademark will be approved and registered, or you'll receive feedback if any changes are needed. Track your application status throughout the process.",
                      },
                    ]}
                  />
                </Card.Content>
              </Card>
            </Cell>
            <Cell>
              <Card>
                <Card.Header title="Frequently Asked Questions" />
                <Accordion
                  size="small"
                  items={faqSections.flatMap((section) => [
                    accordionSectionItemBuilder({
                      title: section.sectionTitle,
                    }),
                    ...section.items.map((item) =>
                      accordionItemBuilder({
                        title: item.question,
                        children: (
                          <Box padding="SP4">
                            <Text size="small" secondary>
                              {item.answer
                                .split("\n")
                                .map((line, index, array) => (
                                  <React.Fragment key={index}>
                                    {line}
                                    {index < array.length - 1 && <br />}
                                  </React.Fragment>
                                ))}
                            </Text>
                          </Box>
                        ),
                      })
                    ),
                  ])}
                />
                <Box padding="SP4" align="center">
                  <TextButton
                    onClick={() => onPricingDisclosureClick?.()}
                    underline="onHover"
                  >
                    View Complete Pricing & Fee Disclosure
                  </TextButton>
                </Box>
              </Card>
            </Cell>
          </Layout>
        </Page.Content>
      </Page>
    </>
  );
};

export default TrademarkRequestsList;
